<template>
    <div class="col-12 mx-auto">
        <div class="card">
            <div class="card-header" v-if="showSettingsBtn">
                <i class="fas fa-cogs pointer fa-2x float-right text-primary"
                   v-if="showSettingsBtn"
                   @click.stop="changeGridSettings"
                   data-toggle="tooltip"
                   :title="settingsTooltipText"></i>

                <grid-user-settings ref="userSettings"
                                    :schemaModelFields="schemaModelFields"
                                    :componentName="componentName"
                                    :useLabelAsTitle="true"
                                    :showUserSettings="true"
                                    :gridOptions="userSettings"
                                    v-if="showUserSettingsForm"
                                    @saveGridSettings="saveGridSettings"
                                    @loadGridSettings="loadGridSettings"
                                    @userSettingsChanged="userSettingsChanged">
                </grid-user-settings>
            </div>

            <div class="btn-toolbar justify-content-between mt-3" role="toolbar">
                <div class="form-inline col-lg-6 col-md-12" v-if="showFilterBtn">
                    <div class="col-3">
                        <button class="btn btn-secondary"
                                @click.stop="filterByDateRange">
                            <i class="fas fa-filter"></i>
                            {{ $t('buttons.filter.btnText') }}
                        </button>
                    </div>
                    <div class="col-9">
                        <kendo-dropdownlist v-model="dateRangeType"
                                            :data-source="dateRangeDatasource"
                                            :data-text-field="isEnVersion ? 'textEn' : 'text'"
                                            :data-value-field="'value'"
                                            :filter="'contains'"
                                            :optionLabel="$t('choose')"
                                            style="width:100%">
                        </kendo-dropdownlist>
                    </div>
                </div>

                <div class="input-group" :class="showFilterBtn? 'col-lg-6 col-md-12': 'col-12'">
                    <input v-model="searchQuery" v-on:keyup.enter.stop="massSearch" v-on:keyup.esc.stop="clearFilters" type="text" class="form-control" aria-label="Mass search input" aria-describedby="massSearch">
                    <button class="btn btn-secondary" @click.stop="massSearch">
                        <i class="fas fa-search"></i>
                        {{ $t('buttons.search.btnText') }}
                    </button>
                    <button class="btn btn-secondary"
                            @click.stop="clearFilters">
                        <i class="fas fa-times"></i>
                        {{ $t('buttons.clearFilters.btnText') }}
                    </button>
                </div>
            </div>

            <div class="card-body" id="grid">
                <kendo-datasource ref="datasource"
                                  :transport="transportSettings"
                                  :filter="filter"
                                  :group="group"
                                  :schema-model-id="'id'"
                                  :schema-model-fields="schemaModelFields"
                                  :schema-total="'total'"
                                  :schema-data="'data'"
                                  :page-size='gridOptions.pageable.size'
                                  :server-paging="true"
                                  :server-sorting="true"
                                  :server-filtering="true"
                                  :error="dataSourceError">
                </kendo-datasource>

                <kendo-grid ref="grid"
                            :data-source-ref="'datasource'"
                            :navigatable="gridOptions.navigatable"
                            :editable="gridOptions.editable"
                            :filterable="gridOptions.filterable"
                            :pageable="gridOptions.pageable"
                            :reorderable="gridOptions.reorderable"
                            :resizable="gridOptions.resizable"
                            :groupable="gridOptions.groupable"
                            :allow-copy="gridOptions.allowCopy"
                            :column-menu="gridOptions.columnMenu"
                            :sortable-mode="gridOptions.sortable.mode"
                            :sortable-allow-unsort="gridOptions.sortable.allowUnsort"
                            :sortable-show-indexes="gridOptions.sortable.showIndexes"
                            :selectable="gridOptions.selectable"
                            :scrollable="gridOptions.scrollable"
                            :toolbar="toolbar"
                            :excel="gridOptions.excel"
                            :pdf="gridOptions.pdf"
                            :dataBound="onDatabound">
                    <kendo-grid-column field="id"
                                       :filterable="false"
                                       :title="$t('registers.events.participants.gridHeaders.id')"
                                       :hidden="true"
                                       :lockable="true"
                                       :locked="false">
                    </kendo-grid-column>
                    <kendo-grid-column field="date"
                                       :title="$t('scheduler.gridHeaders.date')"
                                       :hidden="true"
                                       :lockable="true"
                                       :locked="false"
                                       :format="customDateFormat">
                    </kendo-grid-column>
                    <kendo-grid-column field="timeRange"
                                       :title="$t('scheduler.gridHeaders.timeRange')"
                                       :filterable="false"
                                       :sortable="false"
                                       :lockable="true"
                                       :locked="false">
                    </kendo-grid-column>
                    <kendo-grid-column field="hallName"
                                       :title="$t('scheduler.hall')"
                                       :width="250"
                                       :lockable="true"
                                       :locked="false"
                                       :template="hallNameCellTemplate">
                    </kendo-grid-column>
                    <kendo-grid-column field="venue"
                                       :title="$t('scheduler.venue')"
                                       :lockable="true"
                                       :locked="false"
                                       :template="venueCellTemplate">
                    </kendo-grid-column>
                    <kendo-grid-column field="eventName"
                                       :title="$t('scheduler.gridHeaders.eventName')"
                                       :lockable="true"
                                       :locked="false"
                                       :template="eventNameCellTemplate">
                    </kendo-grid-column>
                    <kendo-grid-column field="title"
                                       :title="$t('scheduler.title')"
                                       :lockable="true"
                                       :locked="false"
                                       :template="titleCellTemplate">
                    </kendo-grid-column>
                    <kendo-grid-column field="description"
                                       :title="$t('scheduler.description')"
                                       :lockable="true"
                                       :locked="false"
                                       :template="descriptionCellTemplate">
                    </kendo-grid-column>
                    <kendo-grid-column field="coordinators"
                                       :title="$t('scheduler.coordinators')"
                                       :lockable="true"
                                       :locked="false"
                                       :template="coordinatorsCellTemplate">
                    </kendo-grid-column>
                    <kendo-grid-column field="lecturers"
                                       :title="$t('scheduler.lecturers')"
                                       :lockable="true"
                                       :locked="false"
                                       :template="lecturersCellTemplate">
                    </kendo-grid-column>
                    <kendo-grid-column v-if="showBtns"
                                       :title="$t('registers.events.participants.gridHeaders.management')"
                                       field="btnsColumn"
                                       :template="btnsTemplate"
                                       :sortable="false"
                                       :filterable="false"
                                       :lockable="false"
                                       :locked="false">
                    </kendo-grid-column>
                    <kendo-grid-column v-if="!showBtns"
                                       :title="$t('actions')"
                                       :template="detailsBtnsTemplate"
                                       :sortable="false"
                                       :filterable="false"
                                       :lockable="false"
                                       :locked="false">
                    </kendo-grid-column>
                </kendo-grid>
            </div>
        </div>

        <DetailsModal v-model="model" :show="showDetails" :allowEdit="false" @onClose="onDetailsClose"></DetailsModal>
    </div>

</template>
<script src="https://kendo.cdn.telerik.com/2019.2.619/js/pako_deflate.min.js"></script>

<script>
    import Vue from 'vue';
    import JSZip from 'jszip'
    import GridUserSettings from "@/components/grid/UserSettings";
    import BtnsTemplate from "@/components/grid/StandardBtnsTemplate";
    import DetailsModal from '@/components/scheduler/details.modal';
    import agendaService from '@/services/agenda.service';
    import { mapGetters } from 'vuex';

    /*
    Renders the grid in "DejaVu Sans" font family, which is
    declared in kendo.common.css. It also declares the paths to the
    fonts below using <tt>kendo.pdf.defineFont</tt>, because the
    stylesheet is hosted on a different domain.
    */
    kendo.pdf.defineFont({
        "DejaVu Sans": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans.ttf",
        "DejaVu Sans|Bold": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans-Bold.ttf",
        "DejaVu Sans|Bold|Italic": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans-Oblique.ttf",
        "DejaVu Sans|Italic": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans-Oblique.ttf",
        "WebComponentsIcons": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/glyphs/WebComponentsIcons.ttf"
    });

    export default {
        name: 'participants-grid',
        components: {
            'grid-user-settings': GridUserSettings,
            DetailsModal
        },
        created: function () {
            window.JSZip = JSZip
        },
        props: {
            showHideButton: {
                default: false,
                type: Boolean
            },
            showPinButton: {
                default: true,
                type: Boolean
            },
            showNewButton: {
                default: true,
                type: Boolean
            },
            showResizeIcon: {
                default: true,
                type: Boolean
            },
            size: {
                default: 6,
                type: Number
            },
            showSettingsBtn: {
                default: true,
                type: Boolean
            },
            showBtns: {
                default: true,
                type: Boolean
            },
            eventId: {
                default: 0,
                type: Number
            },
            showFilterBtn:{
                default: true,
                type: Boolean
            },
        },
        data() {
            return {
                componentName: "HallAgendaGrid",
                settingsTooltipText: this.$t('buttons.settings.tooltipText'),
                customSettingsLoaded: false,
                titleKey: 'sideMenu.agenda',
                dateRangeDatasource: this.$helpers.dateRangesDropdownSource(),
                dateRangeType: '',
                searchQuery: '',
                showDetails: false,
                model: null,
                group: {
                    field: "date",
                },
                group1: [
                    { field: "date" },
                    { field: "hallName" },
                ],
                schemaModelFields: {
                    id: { name: 'id', type: 'number', editable: false, nullable: false, visible: true },
                    hallId: { name: 'hallId', type: 'number', editable: false, nullable: true, visible: true },
                    hallName: { name: 'hallName', type: 'string', editable: false, nullable: true, visible: true },
                    hallCapacity: { name: 'hallCapacity', type: 'number', editable: false, nullable: true, visible: true },
                    venueId: { name: 'venueId', type: 'number', editable: false, nullable: true, visible: true },
                    venue: { name: 'venue', type: 'string', editable: false, nullable: true, visible: true },
                    eventId: { name: 'eventId', type: 'number', editable: false, nullable: true, visible: true },
                    startTime: { name: 'startTime', type: 'date', editable: false, nullable: true, visible: true },
                    endTime: { name: 'endTime', type: 'date', editable: false, nullable: true, visible: true },
                    description: { name: 'description', type: 'string', editable: false, nullable: true, visible: true },
                    title: { name: 'title', type: 'string', editable: false, nullable: true, visible: true },
                    eventName: { name: 'eventName', type: 'string', editable: false, nullable: true, visible: true },
                    timeRange: { name: 'timeRange', type: 'string', editable: false, nullable: true, visible: true },
                    date: { name: 'date', type: 'date', editable: false, nullable: true, visible: true },
                    coordinators: { name: 'coordinators', type: 'string', editable: false, nullable: true, visible: true },
                    lecturers: { name: 'lecturers', type: 'string', editable: false, nullable: true, visible: true },

                },
                transportSettings: {
                    read: {
                        url: this.$store.getters.baseUrl + `/api/HallScheduler/hallEventsIntoDayRanges`,
                        dataType: 'json',
                        type: 'POST',
                        contentType: 'application/json',
                        beforeSend: this.beforeSend
                    },
                    parameterMap: this.parameterMap
                },
                gridOptions: {
                    ...this.$store.getters.gridOptions,
                    excel: {
                        fileName: 'HallAgenda.xlsx',
                        allPages: false,
                        filterable: true,
                        collapsible: true
                    },
                    pdf: {
                        fileName: 'HallAgenda.pdf',
                        title: this.$t(this.titleKey),
                        subject: this.$t(this.titleKey),
                        creator: this.$store.userName,
                        allPages: false,
                        avoidLinks: true,
                        paperSize: 'A4',
                        margin: { top: '2cm', left: '1cm', right: '1cm', bottom: '1cm' },
                        landscape: true,
                        repeatHeaders: true,
                        scale: 0.6
                    }
                },
                working: false,
                showUserSettingsForm: false,
            }
        },
        methods: {
            onDatabound(e) {
                if (!this.customSettingsLoaded) {
                    this.customSettingsLoaded = true;
                    this.loadGridSettings();
                }

                var items = e.sender.items();
                items.each(function (idx, item) {
                    let dataItem = e.sender.dataItem(item);
                    if (dataItem.isAllDay) {
                        // $(item).addClass('left-border-blue');
                    }
                });
            },
            dataSourceError(e) {
                this.$vs.notify({
                    title: this.$t('notifications.error'),
                    text: this.$helpers.kendoDataSourceErrorText(e),
                    color: 'danger',
                    time: this.$store.getters.notificationDuration,
                });
            },
            parameterMap: function (options, operation) {
                if (operation !== 'read' && options.models) {
                    return { models: kendo.stringify(options.models) }
                } else {
                    return kendo.stringify(options);
                }
            },
            saveGridSettings() {
                let grid = this.$refs.grid.kendoWidget();

                if (grid) {
                    this.$refs.userSettings.setGridSettings(grid.getOptions());
                }

                let options = grid.getOptions();

                this.$http.post('/api/personalization/saveGridSettings', {
                    componentName: this.componentName,
                    gridSettings: JSON.stringify(options)
                }).then((response) => {
                    this.$vs.notify({
                        title: response.request ? this.$t('request.response.status.' + response.request.status) : this.$t('request.response.status.200'),
                        text: response.data.message ? response.data.message : (response.request.statusText ? response.request.statusText : response.message),
                        color: 'success',
                        time: this.$store.getters.notificationDuration,
                    })
                }).catch(error => {
                    this.$vs.notify({
                        title: error.response ? this.$t('request.response.status.' + error.response.status) : this.$t('request.response.status.400'),
                        text: error.response.data.message
                            ? error.response.data.message
                            : (error.response.data
                                ? error.response.data
                                : (error.response.statusText ? error.response.statusText : error.message)),
                        color: 'danger',
                        time: this.$store.getters.notificationDuration
                    })
                })
            },
            loadGridSettings() {
                return this.$http.get('/api/personalization/' + this.componentName + '/getGridSettings'
                ).then((response) => {
                    if (response.data.settings === null || !response.data.settings) {
                        return;
                    }

                    try {
                        let options = JSON.parse(response.data.settings);
                        if (!options || !options.hasOwnProperty("columns")) {
                            return;
                        }

                        let grid = this.$refs.grid.kendoWidget();
                        if (grid) {
                            grid.setOptions(options);

                            if (options.pageable.size != grid.dataSource.options.pageSize) {
                                grid.dataSource.pageSize(options.pageable.size);
                                grid.refresh();
                            }

                            this.$refs.userSettings.setGridSettings(grid.getOptions());
                        }

                    } catch (e) {

                    }
                }).catch(error => {
                    this.$vs.notify({
                        title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                        text: this.$helpers.errorText(error),
                        color: 'danger',
                        time: this.$store.getters.notificationDuration
                    })
                })
            },
            userSettingsChanged(userSettings, pageSizeChanged) {
                let grid = this.$refs.grid.kendoWidget();

                if (grid) {
                    try {
                        grid.setOptions(userSettings);
                        if (pageSizeChanged === true) {
                            grid.dataSource.pageSize(userSettings.pageable.size);
                            grid.refresh();
                        }
                    } catch (e) {
                        return;
                    }
                }
            },
            beforeSend: function (req) {
                if (this.token) {
                    req.setRequestHeader('Authorization', 'Bearer ' + this.token);
                }
            },
            changeGridSettings() {
                this.showUserSettingsForm = !this.showUserSettingsForm;
            },
            resize(componentName, size) {
                this.$emit('resize', componentName, size, this.$refs.grid.kendoWidget())
            },
            hide(componentName) {
                this.$emit('hide', componentName)
            },
            pinned(componentName) {
                this.$emit('pinned', componentName)
            },
            unpinned(componentName) {
                this.$emit('unpinned', componentName)
            },
            btnsTemplate: function (e) {
                return {
                    template: Vue.component(BtnsTemplate.name, BtnsTemplate),
                    templateArgs: {
                        ...e,
                        btns: [
                            {
                                name: "edit",
                                text: "",
                                tooltip: this.$t('buttons.edit.btnText'),
                                class: 'btn btn-primary',
                                icon: "fas fa-edit",
                                show: this.hasEditRights,
                                clickHandler: this.editBtnClick
                            },
                            {
                                name: "details",
                                text: "",
                                tooltip: this.$t('buttons.details.btnText'),
                                class: 'btn btn-primary',
                                icon: "fas fa-eye",
                                show: this.hasReadRights,
                                clickHandler: this.detailsBtnClick
                            },
                            {
                                name: "delete",
                                text: "",
                                tooltip: this.$t('buttons.delete.btnText'),
                                class: 'btn btn-danger',
                                icon: "fas fa-times",
                                show: this.hasDeleteRights,
                                clickHandler: this.deleteBtnClick
                            },
                        ]
                    }
                };
            },
            detailsBtnsTemplate: function (e) {
                return {
                    template: Vue.component(BtnsTemplate.name, BtnsTemplate),
                    templateArgs: {
                        ...e,
                        btns: [
                            {
                                name: "details",
                                text: "",
                                tooltip: this.$t('buttons.details.btnText'),
                                class: 'btn btn-primary',
                                icon: "fas fa-eye",
                                show: this.hasReadRights,
                                clickHandler: this.detailsBtnClick
                            }
                        ]
                    }
                };
            },
            detailsBtnClick(args) {
                if (!this.hasReadRights) {
                    return
                }

                this.$emit('detailsBtnClick', args)

                agendaService
                    .getById(args.id)
                    .then(response => {
                        this.model = response.data.model;
                        this.showDetails = true;
                    })
                    .catch(error => {
                        this.model = null;
                        this.showDetails = false;

                        this.$vs.notify({
                            title: this.componentTitle + ' - ' + this.$t('request.response.status.' + statusCode),
                            text: this.$helpers.errorText(error),
                            color: this.$helpers.notificationColor(statusCode),
                            time: this.$store.getters.notificationDuration
                        });
                    });


            },
            onDetailsClose() {
                this.model = null;
                this.showDetails = false;
            },
            editBtnClick(args) {
                if (this.hasEditRights) {
                    this.$emit('editBtnClick', args)
                }
            },
            deleteBtnClick(args) {
                if (this.hasDeleteRights) {
                    this.$emit('deleteBtnClick', args)
                }
            },
            refresh() {
                const grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    grid.dataSource.read();
                }
            },
            filterByDateRange() {
                const grid = this.$refs.grid.kendoWidget();
                if (!grid) {
                    this.clearFilters();
                    return;
                }

                let rangeType = Number(this.dateRangeType);
                if (isNaN(rangeType) || rangeType < 0) {
                    this.clearFilters();
                    return;
                }

                let filter = this.generateFilters(rangeType);
                grid.dataSource.filter(filter);
            },
            generateFilters(rangeType) {
                if (rangeType === 0) {
                    var dateToCompare = this.$helpers.calculateDateRange(rangeType, false);
                    if (dateToCompare) {
                        let filter = { logic: 'and', filters: [] };
                        filter.filters.push({
                            field: 'date',
                            operator: 'eq',
                            value: dateToCompare
                        });

                        if (this.eventId && this.eventId > 0) {
                            filter.filters.push({
                                field: 'EventId',
                                operator: 'eq',
                                value: this.eventId
                            })
                        }

                        return filter;
                    }
                }

                if (rangeType === 1 || rangeType === 2) {
                    //this week || last week
                    let result = this.$helpers.calculateDateRange(rangeType, false);
                    if (result.startTime && result.endTime) {
                        let filter = { logic: 'and', filters: [] };
                        filter.filters.push({
                            field: 'date',
                            operator: 'gte',
                            value: result.startTime
                        });

                        filter.filters.push({
                            field: 'date',
                            operator: 'lt',
                            value: result.endTime
                        });

                        if (this.eventId && this.eventId > 0) {
                            filter.filters.push({
                                field: 'EventId',
                                operator: 'eq',
                                value: this.eventId
                            })
                        }

                        return filter;
                    }
                }

                return;
            },
            clearFilters() {
                this.searchQuery = '';
                const grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    grid.dataSource.sort({});
                    grid.dataSource.page(1);
                    grid.dataSource.aggregate([]);

                    grid.dataSource.filter(this.filter);
                }

                this.dateRangeType = '';
            },
            massSearch() {
                if (!this.searchQuery) {
                    this.$vs.notify({
                        title: this.$t('buttons.search.btnText'),
                        text: this.$t('emptySearchFilter'),
                        color: 'warning',
                        time: this.$store.getters.notificationDuration
                    })

                    return;
                }

                let grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    let filter = this.$helpers.getMassFilterForGrid(grid, this.searchQuery);
                    if (filter) {
                        grid.dataSource.filter(filter);
                    }
                }
            },
            hallNameCellTemplate: function (dataItem) {
                let templateHtml = '<span title="' + (dataItem.hallName ? dataItem.hallName.replace('"', '\'') : '') + '">' + (dataItem.hallName ? dataItem.hallName : "") + '</span>';

                return templateHtml;
            },
            venueCellTemplate: function (dataItem) {
                let templateHtml = '<span title="' + (dataItem.venue ? dataItem.venue.replace('"', '\'') : '') + '">' + (dataItem.venue ? dataItem.venue : "") + '</span>';

                return templateHtml;
            },
            titleCellTemplate: function (dataItem) {
                let templateHtml = '<span title="' + (dataItem.title ? dataItem.title.replace('"', '\'') : '') + '">' + (dataItem.title ? dataItem.title : "") + '</span>';

                return templateHtml;
            },
            eventNameCellTemplate: function (dataItem) {
                let templateHtml = '<span title="' + (dataItem.eventName ? dataItem.eventName.replace('"', '\'') : '') + '">' + (dataItem.eventName ? dataItem.eventName : "") + '</span>';

                return templateHtml;
            },
            descriptionCellTemplate: function (dataItem) {
                let templateHtml = '<span title="' + (dataItem.description ? dataItem.description.replace('"', '\'') : '') + '">' + (dataItem.description ? dataItem.description : "") + '</span>';

                return templateHtml;
            },
            coordinatorsCellTemplate: function (dataItem) {
                let templateHtml = '<span title="' + (dataItem.coordinators ? dataItem.coordinators.replace('"', '\'') : '') + '">' + (dataItem.coordinators ? dataItem.coordinators : "") + '</span>';

                return templateHtml;
            },
            lecturersCellTemplate: function (dataItem) {
                let templateHtml = '<span title="' + (dataItem.lecturers ? dataItem.lecturers.replace('"', '\'') : '') + '">' + (dataItem.lecturers ? dataItem.lecturers : "") + '</span>';

                return templateHtml;
            }
        },
        computed: {
            ...mapGetters(['isEnVersion', 'dateFormat']),
            customDateFormat() {
                return `{0: ${this.dateFormat}}`;
            },
            token: function () {
                return this.$store.getters.authToken;
            },
            userSettings() {
                let grid = this.$refs.grid.kendoWidget();
                return grid.getOptions();
            },
            hasReadRights() {
                return this.$store.getters.claims.agenda.read;
            },
            hasEditRights() {
                return this.$store.getters.claims.agenda.create
                    || this.$store.getters.claims.agenda.update;
            },
            hasDeleteRights() {
                return this.$store.getters.claims.agenda.delete;
            },
            filter() {
                if (!this.eventId) {
                    return {};
                }

                return {
                    logic: "and", filters: [
                        {
                            field: 'EventId',
                            operator: 'eq',
                            value: this.eventId
                        }]
                };
            },
            toolbar: function () {
                return [{ name: 'excel', text: this.$t('buttons.excelExport.tooltipText') }]
            },
        }
    }
</script>


<style>
    /*
        Use the DejaVu Sans font for display and embedding in the PDF file.
        The standard PDF fonts have no support for Unicode characters.
    */
    .k-pdf-export .k-grid {
        font-family: "DejaVu Sans", "Arial", sans-serif;
    }

    .k-pdf-export .k-filterable .k-grid-filter {
        display: none;
    }

    .k-pdf-export .k-with-icon .k-header-column-menu {
        display: none;
    }

    .k-grid .k-grid-header .k-header .k-link {
        height: auto;
    }

    .k-grid .k-grid-header .k-header {
        white-space: normal;
    }

    .k-grid-edit, .k-grid-details, .k-grid-delete, .k-grid-pdf, .k-grid-excel {
        padding: 0;
        margin-bottom: 2px;
    }

    .k-grid td {
        white-space: nowrap;
        text-overflow: ellipsis;
    }
</style>

<style lang="scss">
    tr.left-border-danger {
        &:hover {
            font-weight: bold;
            font-size: 1.3em;
            color: rgba(255, 0, 0, 0.7) !important;
        }

        td:first-child {
            border-left: 7px solid rgba(255, 0, 0, 0.5) !important;
        }
    }

    tr.left-border-warning {
        &:hover {
            font-weight: bold;
            font-size: 1.3em;
            color: rgba(255, 127, 7, 0.7) !important;
        }

        td:first-child {
            border-left: 7px solid rgba(255, 127, 7, 0.5) !important;
        }
    }

    tr.left-border-blue {
        &:hover {
            font-weight: bold;
            font-size: 1.3em;
            color: #3498db !important;
        }

        td:first-child {
            border-left: 7px solid #3498db !important;
        }
    }

    tr.left-border-green {
        &:hover {
            font-weight: bold;
            font-size: 1.3em;
            color: #4caf50 !important;
        }

        td:first-child {
            border-left: 7px solid #4caf50 !important;
        }
    }
</style>
